<!--
 * @Author: Nemo
 * @Description: OnTextarea组件 - 统一风格的基础文本域组件
 * @Date: 2025-01-25
 * @LastEditors:
 * @FilePath: /rhcn-checkout-web/src/componentsV2/base/textarea/index.vue
-->
<template>
  <div class="on-textarea-container" :class="{ 'is-error': error, 'is-disabled': disabled }">
    <div class="textarea-wrapper">
      <textarea
        class="on-textarea"
        :class="{ focus: isFocus, disabled, error: error, 'no-resize': !resizable }"
        :style="{ '--focus-border-color': focusBorderColor, ...customStyle }"
        v-bind="$attrs"
        :value="value"
        :placeholder="currentPlaceholder"
        :disabled="disabled"
        :rows="rows"
        :maxlength="maxLength"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      ></textarea>
      <div class="textarea-suffix" v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'OnTextarea',
  inheritAttrs: false,
  props: {
    // 输入值
    value: {
      type: [String, Number],
      default: '',
    },
    // 占位文本
    placeholder: {
      type: String,
      default: '',
    },
    // 禁用状态
    disabled: {
      type: Boolean,
      default: false,
    },
    // 错误状态
    error: {
      type: Boolean,
      default: false,
    },
    // 显示行数
    rows: {
      type: [String, Number],
      default: 3,
    },
    // 是否在聚焦时清空placeholder
    clearPlaceholderOnFocus: {
      type: Boolean,
      default: true,
    },
    // 最大输入长度
    maxLength: {
      type: [Number, String],
      default: undefined,
    },
    // 是否在输入时触发change事件
    inputHandler: {
      type: Function,
      default: () => {},
    },
    // 是否允许拉伸调整大小
    resizable: {
      type: Boolean,
      default: true,
    },
    // 自定义样式对象
    customStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isFocus: false,
      originalPlaceholder: '',
      emptiedPlaceholder: false,
    };
  },
  mounted() {
    this.originalPlaceholder = this.placeholder;
  },
  watch: {
    placeholder(newVal) {
      // 当placeholder属性变化时更新原始值
      if (!this.isFocus || !this.emptiedPlaceholder) {
        this.originalPlaceholder = newVal;
      }
    },
  },
  computed: {
    ...mapGetters(['app']),
    /**
     * 获取主题色
     * 优先使用app.brandInfo.color，如果不存在则使用$color-text-primary
     * @returns {String} 主题色值
     */
    themeColor() {
      return this.app && this.app.brandInfo && this.app.brandInfo.color
        ? this.app.brandInfo.color
        : '';
    },
    /**
     * 获取聚焦时的边框颜色
     * 优先使用themeColor，否则返回空（将使用CSS中的$color-purple-primary）
     */
    focusBorderColor() {
      return this.themeColor || '';
    },
    /**
     * 获取当前应显示的placeholder
     * 如果聚焦且clearPlaceholderOnFocus为true则为空字符串
     * 否则显示原始placeholder
     */
    currentPlaceholder() {
      return (this.isFocus && this.clearPlaceholderOnFocus) ? '' : this.originalPlaceholder;
    },
  },
  methods: {
    /**
     * 处理输入事件
     * @param {Event} e - 输入事件对象
     */
    handleInput(e) {
      // 如果提供了自定义输入处理函数，先调用它
      if (typeof this.inputHandler === 'function') {
        this.inputHandler(e);
      }

      // 手动强制执行maxLength限制，因为上面的inputHandler可能会修改value
      if (this.maxLength !== undefined && e.target.value.length > this.maxLength) {
        e.target.value = e.target.value.substring(0, this.maxLength);
      }

      this.$emit('input', e.target.value);
    },
    /**
     * 处理聚焦事件
     * @param {Event} e - 聚焦事件对象
     */
    handleFocus(e) {
      this.isFocus = true;
      if (this.clearPlaceholderOnFocus) {
        this.emptiedPlaceholder = true;
      }
      this.$emit('focus', e);
    },
    /**
     * 处理失焦事件
     * @param {Event} e - 失焦事件对象
     */
    handleBlur(e) {
      this.isFocus = false;
      this.emptiedPlaceholder = false;
      this.$emit('blur', e);

      // 在失焦时触发change事件，符合HTML表单标准行为
      this.$emit('change', e);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/variables.scss';

.on-textarea-container {
  width: 100%;
  position: relative;

  .textarea-wrapper {
    position: relative;
    width: 100%;
  }

  .textarea-suffix {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: flex-start;
    z-index: 1;
  }

  .on-textarea {
    width: 100%;
    min-height: 100px;
    background: #FFFFFF;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    border: 1px solid #D0D5DD;
    padding: 12px;
    padding-right: 40px; /* 为suffix预留空间 */
    font-size: 16px;
    color: $color-text;
    transition: all 0.3s;
    outline: none;
    box-sizing: border-box;
    resize: vertical;
    font-family: inherit;
    line-height: 1.5;

    &::placeholder {
      color: #8D98AF;
      font-size: 16px;
    }

    // 聚焦状态
    &.focus {
      border-color: var(--focus-border-color, $color-purple-primary);
      box-shadow: 0px 0px 0px 3px #F1F1FF;
    }

    // 禁用状态
    &.disabled {
      background-color: #F0F3F6;
      color: #9EA9C1;
      cursor: not-allowed;
      resize: none;
    }

    // 禁用拉伸
    &.no-resize {
      resize: none;
    }

    // 错误状态
    &.error {
      border-color: #F53F3F;
      box-shadow: none;
    }
  }

  // 错误信息过渡动画（下方）
  .slide-fade-enter-active {
    transition: all 0.3s ease;
  }
  .slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }
  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
  }
}
</style>
